import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { P } from '@osmosis/react-ui/components/Text';
import { useQuizContext } from '../../context/QuizContext';
import { useLearnAccess } from '../../context/LearnAccessContext';
import { useUserContext } from '../../context/UserContext';
import { Button } from '@osmosis/react-ui/components/Button';
import { ButtonProps } from '@osmosis/react-ui/components/Button';
import { Box, Flex } from '@osmosis/react-ui/components/Box';
import ProgressBar from '../ProgressBar';
import { shortcutsIds } from '../../constants/quizShortcuts';

const Btn = ({ id, onNav, ...p }: ButtonProps & { onNav: () => void }) => {
  return (
    <Button
      variant="outline"
      color="gray40"
      {...p}
      onClick={onNav}
      data-testid={p?.['data-testid'] || ''}
    />
  );
};
type Props = {
  onCloseLockoutModal?: () => void;
};

/**
 * A component which allows you to navigate to previous and next questions during a quiz and displays your progress.
 * @returns  {React.ReactNode} A component made up of the ProgressBar Component and two buttons which allow you to navigate.
 */
const QuizNavigator = ({ onCloseLockoutModal }: Props) => {
  const {
    changeItem,
    currentIndex,
    totalItems,
    correctItems,
    incorrectItems,
    isComplete,
    isDailyPractice,
  } = useQuizContext();

  const { handleQuestionsAccess } = useLearnAccess();
  const { user, hasPrime } = useUserContext();

  const intl = useIntl();
  const current = `${currentIndex + 1}`;
  const total = `${totalItems}`;
  const totalCorrectItems = correctItems()?.length || 0;
  const totalIncorrectItems = incorrectItems()?.length || 0;

  const correctMsg = intl.formatMessage(
    {
      id: 'quiz.navigator.progress.tooltip.correct',
      defaultMessage: '{totalCorrectItems} Correct',
    },
    { totalCorrectItems },
  );
  const incorrectMsg = intl.formatMessage(
    {
      id: 'quiz.navigator.progress.tooltip.incorrect',
      defaultMessage: '{totalIncorrectItems} Incorrect',
    },
    { totalIncorrectItems },
  );

  const progressBarTooltipMessage = (
    color: string,
    addSpace = false,
    sx = {},
  ) => (
    <P
      color={color}
      sx={{ display: 'flex', flexDirection: 'column', ...sx }}
      aria-hidden="true"
    >
      <span>{correctMsg}</span>
      {addSpace && <span>&nbsp;</span>}
      <span>{incorrectMsg}</span>
    </P>
  );

  const ariaLabelText = `${correctMsg} ${incorrectMsg}`;

  const handlerNext = () => {
    if ((user && hasPrime) || currentIndex < 1 || !user) {
      changeItem(1);
    } else if (user && !hasPrime) {
      handleQuestionsAccess(null, null, false, onCloseLockoutModal);
    }
  };
  if (isComplete) {
    return (
      <Box width="100%" my={2}>
        <P textAlign="center" color="black">
          <FormattedMessage
            id="quiz.navigator.complete"
            defaultMessage="Quiz complete"
          />
        </P>
      </Box>
    );
  } else
    return (
      <>
        {!isComplete && !isDailyPractice && (
          <Btn
            id={shortcutsIds.prev}
            data-testid="nav-prev"
            color="text"
            onNav={() => changeItem(-1)}
          >
            <FormattedMessage id="quiz.navigator.prev" defaultMessage="Prev" />
          </Btn>
        )}
        <Box width={1} mx={3}>
          <P data-testid="current-text" textAlign="center" color="gray50">
            <FormattedMessage
              id="quiz.navigator.progress_text"
              defaultMessage="{current} of {total}"
              values={{ current, total }}
            />
          </P>
          <Flex flexDirection="column" alignItems="center">
            <ProgressBar
              progress={totalCorrectItems / totalItems || 0}
              progress2={totalIncorrectItems / totalItems || 0}
              height={13}
              barColor="green"
              tooltipMessage={progressBarTooltipMessage('white')}
            />
            <Box aria-label={ariaLabelText} tabIndex={0}>
              {progressBarTooltipMessage('gray50', true, {
                flexDirection: 'row',
              })}
            </Box>
          </Flex>
        </Box>
        {!isComplete && !isDailyPractice && (
          <Btn
            data-testid="nav-next"
            color="text"
            id={shortcutsIds.next}
            onNav={handlerNext}
          >
            <FormattedMessage id="quiz.navigator.next" defaultMessage="Next" />
          </Btn>
        )}
      </>
    );
};

export default QuizNavigator;
